import React from 'react';
import { get, isUndefined } from 'lodash';
import { useSelector } from 'react-redux';
import moment from 'moment';
import styled from '@emotion/styled';

import defaultFieldRenderer from './default-field-renderer';

import {
  AutoComplete,
  Checkbox,
  CheckboxGroup,
  DatePicker,
  DocumentTypeMetadataTitle,
  FileUpload,
  FormElementChangeEvent,
  Grade,
  Input,
  LinkToPage,
  MultiSelectPhotos,
  NumberInput,
  OnBlurCallback,
  Panel,
  PanelAddress,
  PanelDateTime,
  PanelDocumentUpload,
  PanelLocation,
  PanelProfile,
  Password,
  PhoneNumber,
  RadioGroup,
  RichTextEditor,
  Hidden,
  SSN,
  Select,
  TextArea,
  DragNDropOptions,
} from '..';
import {
  ETHNICITIES_PROFILE_FIELD_NAME,
  ETHNICITIES_PROFILE_FIELD_NONE_OF_ABOVE_OPTION_VALUE,
  MAX_MOBILE_WIDTH_FORM_COMPONENT,
  MAX_WIDTH_FORM_COMPONENT,
  hasMaxLength as getHasMaxLength,
  hasPlaceholder as getHasPlaceholder,
  hasValueType as getHasValueType,
  isPanel as getIsPanel,
  getPath,
  hasStyle,
} from '../../../utils/form';
import {
  FieldErrors,
  FormComponentType,
  ICorrectionComment,
  IElementSchema,
  IHiddenFields,
  IMemo,
} from '../../../types/form';
import { Label, Tooltip } from '../..';
import DropdownAutoComplete from '../dropdown-autocomplete';
import { IMatcher } from '../../../utils/matchers/IMatcher';
import MatcherFactory from '../../../utils/matchers/MatcherFactory';
import { OptionRecord } from '../../../types/models/user-management';
import { getAllLookupsFromState } from '../../../state/selectors/lookup';
import { hasRequiredValidation } from '../../../utils/form-builder';
import { isISOString } from '../../../utils/date';
import { useResponsive } from '../../../hooks/use-responsive';

export type ElementListProps = {
  elements: IElementSchema[];
  errors?: FieldErrors<Record<string, any>>;
  hiddenFields?: IHiddenFields;
  path?: string;
  getFormData: (path: string) => any;
  fieldRenderer?: (
    type: FormComponentType,
    attributes: any,
    error: any,
    Comp: React.ComponentType<any>,
    index?: number
  ) => React.ReactNode;
  itemIndex?: number;
  onBlurElement?: OnBlurCallback;
  onChangeElement?: (event: FormElementChangeEvent, memo?: IMemo) => void | Promise<void>;
  onVisibilityChange?: (name: string, path: string) => void;
  onAddItem?: (name: string, path: string, index: number) => void;
  onDeleteItem?: (name: string, path: string, index: number) => void;
  isAdminView?: boolean;
  enabledComponents?: any;
  correctionComments?: ICorrectionComment[];
  setMultiDocUploadInProgress: (state: boolean) => void;
  multiDocUploadInProgress: boolean;
};
export const OptionalLabel = styled.span`
  color: rgba(0, 0, 0, 0.5);
  font-weight: normal;
`;

export const InstructiveLabel = styled.div<{ isLabel?: boolean }>`
  color: #000;
  font-size: 16px;
  line-height: 1.5;
  margin-top: ${(props) => (props.isLabel ? '-4px' : '-7px')};
  margin-bottom: 10px;
  white-space: pre-wrap; /* support line break */
`;

export function getComponentByType({ type, inputType, preventSearchStringSave }: IElementSchema) {
  switch (type) {
    case FormComponentType.Panel:
      return Panel;
    case FormComponentType.Label:
      return Label;
    case FormComponentType.DocumentTypeMetadataTitle:
      return DocumentTypeMetadataTitle;
    case FormComponentType.Text:
      if (inputType === 'password') {
        return Password;
      }
      return Input;
    case FormComponentType.Textarea:
      return TextArea;
    case FormComponentType.Dropdown:
      return Select;
    case FormComponentType.Checkbox:
      return Checkbox;
    case FormComponentType.Checkboxgroup:
      return CheckboxGroup;
    case FormComponentType.Radiogroup:
      return RadioGroup;
    case FormComponentType.Date:
      return DatePicker;
    case FormComponentType.Number:
      return NumberInput;
    case FormComponentType.Address:
      return PanelAddress;
    case FormComponentType.ProfileInfo:
      return PanelProfile;
    case FormComponentType.RecommenderInfo:
    case FormComponentType.CounselorInfo:
      return Panel;
    case FormComponentType.Grade:
      return Grade;
    case FormComponentType.PhoneNumber:
      return PhoneNumber;
    case FormComponentType.RichTextEditor:
      return RichTextEditor;
    case FormComponentType.Hidden:
      return Hidden;
    case FormComponentType.DateTime:
      return PanelDateTime;
    case FormComponentType.AutoComplete:
      if (preventSearchStringSave) {
        return DropdownAutoComplete;
      }
      return AutoComplete;
    case FormComponentType.LinkToPage:
      return LinkToPage;
    case FormComponentType.Location:
      return PanelLocation;
    case FormComponentType.SSN:
      return SSN;
    case FormComponentType.MultiSelectPhotos:
      return MultiSelectPhotos;
    case FormComponentType.FileUpload:
      return FileUpload;
    case FormComponentType.DocumentUploadPanel:
      return PanelDocumentUpload;
    case FormComponentType.ReaderQuiz:
    case FormComponentType.CoachQuiz:
    case FormComponentType.InterviewerQuiz:
    case FormComponentType.MentorQuiz:
      return Panel;
    case FormComponentType.DragNDropOptionPanel:
      return DragNDropOptions;
    case FormComponentType.FERPAStatement:
      return Panel;
    case FormComponentType.FinancialPortal:
      return Panel;
    default:
      return null;
  }
}

export const VALIDATE_ON_CHANGE = [
  FormComponentType.Dropdown,
  FormComponentType.Checkbox,
  FormComponentType.Checkboxgroup,
  FormComponentType.Radiogroup,
  FormComponentType.Date,
  FormComponentType.AutoComplete,
  FormComponentType.MultiSelectPhotos,
  FormComponentType.FileUpload,
  FormComponentType.DocumentUploadPanel,
  FormComponentType.ReaderQuiz,
  FormComponentType.CoachQuiz,
  FormComponentType.InterviewerQuiz,
  FormComponentType.MentorQuiz,
  FormComponentType.DragNDropOptionPanel,
];

export const VALIDATE_ON_BLUR = [
  FormComponentType.Text,
  FormComponentType.Textarea,
  FormComponentType.Dropdown,
  FormComponentType.AutoComplete,
  FormComponentType.Checkbox,
  FormComponentType.Checkboxgroup,
  FormComponentType.Radiogroup,
  FormComponentType.Date,
  FormComponentType.Number,
  FormComponentType.Panel,
  FormComponentType.Address,
  FormComponentType.ProfileInfo,
  FormComponentType.RecommenderInfo,
  FormComponentType.CounselorInfo,
  FormComponentType.DateTime,
  FormComponentType.PhoneNumber,
  FormComponentType.Grade,
  FormComponentType.RichTextEditor,
  FormComponentType.Location,
  FormComponentType.SSN,
  FormComponentType.ReaderQuiz,
  FormComponentType.CoachQuiz,
  FormComponentType.InterviewerQuiz,
  FormComponentType.MentorQuiz,
];

export const TEXT_BOX_MAX_LENGTH = 500;

const STANDARD_STYLE = { maxWidth: `${MAX_WIDTH_FORM_COMPONENT}px` };
const STANDARD_MOBILE_STYLE = { maxWidth: `${MAX_MOBILE_WIDTH_FORM_COMPONENT}px` };

function evaluateConditions(
  conditions: any,
  defaultValue: boolean,
  getValue: (path: string) => any,
  lookupsData: OptionRecord,
  itemIndex?: number
) {
  if (typeof conditions === 'boolean') {
    // TODO: a little hacky, this is to handle force disabling specific fields
    return conditions;
  }

  if (!conditions) {
    return defaultValue;
  }

  if (itemIndex !== undefined) {
    if (conditions.fieldName) {
      const arrayConditions = JSON.parse(JSON.stringify(conditions));

      const fieldNameArray = arrayConditions.fieldName.split('-|-');

      fieldNameArray[0] = `${fieldNameArray[0]}[${itemIndex}]`;

      arrayConditions.fieldName = fieldNameArray.join('-|-');

      const matcher: IMatcher = MatcherFactory.create(arrayConditions, lookupsData);
      return matcher.match((fieldName) => getValue(fieldName));
    }
  } else {
    const matcher: IMatcher = MatcherFactory.create(conditions, lookupsData);
    return matcher.match((fieldName) => getValue(fieldName));
  }
}

export function convertElementToProps({
  element,
  path: parentPath,
  disabled = false,
  errors,
  hiddenFields,
  itemIndex,
  isMobile,
  getFormData,
  onChange,
  onVisibilityChange,
  onAddItem,
  onDeleteItem,
  correctionComments,
  setMultiDocUploadInProgress,
  multiDocUploadInProgress,
}: {
  element: IElementSchema;
  path?: string;
  disabled?: boolean;
  errors?: FieldErrors<Record<string, any>>;
  hiddenFields?: IHiddenFields;
  itemIndex?: number;
  isMobile?: boolean;
  getFormData?: (path: string) => any;
  onChange?: (event: FormElementChangeEvent, memo?: IMemo) => void;
  onVisibilityChange?: (name: string, path: string) => void;
  onAddItem?: (name: string, path: string, index: number) => void;
  onDeleteItem?: (name: string, path: string, index: number) => void;
  correctionComments?: ICorrectionComment[];
  setMultiDocUploadInProgress: (state: boolean) => void;
  multiDocUploadInProgress: boolean;
}) {
  const {
    type,
    inputType,
    valueType,
    name,
    title,
    options,
    hasCheckAll,
    hasOtherOption,
    placeHolder,
    maxLength,
    repeatable,
    isOrderedList,
    validation,
    elements,
    tooltip,
    instructiveLabel,
    canPopulateFromProfile,
    showTime,
    dataSource,
    url,
    linkLabel,
    maxRepeatable,
    documentType,
    maxFileSize,
    maxUploadLimit,
    pullFromDocBank,
    currencySymbol,
    preventSearchStringSave,
    decimalPlaces,
    isDeselectable,
    hiddenValue,
  } = element;
  const path = getPath(name, parentPath);
  const value = getFormData && getFormData(path);

  const props: any = {
    name,
    onChange,
    [type === FormComponentType.Checkbox ? 'checked' : 'value']: value,
    disabled,
    instructiveLabel,
  };

  const isRequired = hasRequiredValidation(validation);
  props['aria-required'] = isRequired;
  const hasPlaceholder = getHasPlaceholder(element);
  const hasMaxLength = getHasMaxLength(element);
  const hasValueType = getHasValueType(element);
  const isPanel = getIsPanel(type);
  const isAddressPanel = type === FormComponentType.Address;
  const error = isPanel ? null : get(errors, path);
  const isLinkToPage = type === FormComponentType.LinkToPage;
  const isDocumentTypeMetadataTitle = type === FormComponentType.DocumentTypeMetadataTitle;

  if (!isUndefined(itemIndex)) {
    props.itemIndex = itemIndex;
  }

  if (isPanel) {
    props.repeatable = repeatable;
    props.getFormData = getFormData;
    props.elements = elements;
    props.errors = errors;
    props.hiddenFields = hiddenFields;
    props.path = path;
    props.onVisibilityChange = onVisibilityChange;
    props.onAddItem = onAddItem;
    props.onDeleteItem = onDeleteItem;
    props.maxRepeatable = maxRepeatable;
    if (repeatable) {
      props.isOrderedList = isOrderedList;
    }
    // Only pass the applicable comments through to the file upload component
    const correctionCommentsForUpload = correctionComments?.find(
      (correctionComment: ICorrectionComment) => correctionComment.key === name
    );

    const nestedCorrectionCommentsForUpload = correctionComments?.find((correctionComment: ICorrectionComment) =>
      elements?.find((el) => {
        return el.name === correctionComment.key;
      })
    );
    if (correctionCommentsForUpload) {
      props.correctionComments = [correctionCommentsForUpload];
    } else if (nestedCorrectionCommentsForUpload) {
      props.correctionComments = correctionComments;
    }
  }

  if (hasStyle(type)) {
    props.style = isMobile ? STANDARD_MOBILE_STYLE : STANDARD_STYLE;
  }
  if (type === FormComponentType.ProfileInfo) {
    props.canPopulateFromProfile = canPopulateFromProfile;
  }
  if (type === FormComponentType.AutoComplete) {
    props.preventSearchStringSave = preventSearchStringSave;
  }
  if (type === FormComponentType.DocumentUploadPanel) {
    props.documentType = documentType;
    props.setMultiDocUploadInProgress = setMultiDocUploadInProgress;
    props.multiDocUploadInProgress = multiDocUploadInProgress;
  }
  if (type === FormComponentType.Number) {
    props.placeholder = placeHolder;
    props.currencySymbol = currencySymbol;
  }

  if (type === FormComponentType.Grade) {
    props.decimalPlaces = decimalPlaces;
  }

  if (type === FormComponentType.FileUpload) {
    props.setMultiDocUploadInProgress = setMultiDocUploadInProgress;
    props.multiDocUploadInProgress = multiDocUploadInProgress;
    props.documentType = documentType;
    props.maxFileSize = maxFileSize;
    props.maxUploadLimit = maxUploadLimit;
    props.pullFromDocBank = pullFromDocBank;
    // If an admin has provided correction comments, display them above the file upload
    if (correctionComments?.length) {
      props.label = correctionComments[0].comment || '';
    }
  }
  if (type === FormComponentType.Label) {
    props.children = tooltip ? <Tooltip title={tooltip}>{title}</Tooltip> : title;
    props.style = { fontSize: 16 };
  }
  if (type === FormComponentType.Text) {
    props.inputType = inputType;
    props.maxLength = TEXT_BOX_MAX_LENGTH;
  }
  if (type === FormComponentType.Textarea) {
    props.maxLength = TEXT_BOX_MAX_LENGTH;
  }
  if (type === FormComponentType.Checkboxgroup) {
    props.hasCheckAll = hasCheckAll;
  }
  if (type === FormComponentType.Radiogroup) {
    props.hasOtherOption = hasOtherOption;
    props.isDeselectable = isDeselectable;
  }
  if (type === FormComponentType.Date) {
    props.showTime = showTime;
  }
  if (type === FormComponentType.Hidden) {
    props.hiddenValue = hiddenValue;
  }
  if (options) {
    props.options = options;
  } else if (dataSource) {
    props.dataSource = dataSource;
  }
  if (hasPlaceholder) {
    props.placeholder = placeHolder;
  }
  // only set if `maxLength` has a value
  if (hasMaxLength && maxLength) {
    props.maxLength = maxLength;
  }
  if (hasValueType) {
    props.valueType = valueType;
  }

  if (!isLinkToPage && !isDocumentTypeMetadataTitle && !isAddressPanel) {
    if (title) {
      props.label =
        title &&
        (isRequired || isPanel ? (
          title
        ) : (
          <>
            <span>{title}</span>
            <OptionalLabel>&nbsp;(Optional)</OptionalLabel>
          </>
        ));
    }
  } else if (isAddressPanel && title) {
    props.label = <span>{title}</span>;
  } else if (isLinkToPage) {
    props.url = url;
    props.linkLabel = linkLabel;
  } else if (isDocumentTypeMetadataTitle) {
    props.title = title;
  }

  if (tooltip) {
    if (!isLinkToPage) {
      props.label = <Tooltip title={tooltip}>{props.label}</Tooltip>;
    } else {
      props.label = tooltip;
    }
  }

  return { props, error };
}

const ElementList = React.memo(function ElementList({
  elements,
  errors = {},
  hiddenFields,
  path,
  getFormData,
  fieldRenderer = defaultFieldRenderer,
  itemIndex,
  onBlurElement,
  onChangeElement,
  onVisibilityChange,
  isAdminView = false,
  onAddItem,
  onDeleteItem,
  enabledComponents,
  correctionComments,
  setMultiDocUploadInProgress,
  multiDocUploadInProgress,
}: ElementListProps) {
  let index = -1;
  const { lg } = useResponsive();
  const isMobile = !lg;
  const lookupsData = useSelector(getAllLookupsFromState);
  const excluded = (enabledComponents: any, element: IElementSchema) => {
    enabledComponents = enabledComponents ?? [];
    if (enabledComponents.includes(element.name)) {
      return true;
    }

    if (element.elements) {
      return element.elements.some((nestedElement: IElementSchema) => {
        if (nestedElement.documentType) {
          if (enabledComponents.includes(nestedElement.name)) {
            return true;
          }
        }
      });
    }
    return false;
  };

  return (
    <>
      {elements.map((element: IElementSchema) => {
        const { id, type, name, visible, disable } = element;
        const { props, error } = convertElementToProps({
          path,
          element,
          disabled: enabledComponents
            ? !excluded(enabledComponents, element)
            : evaluateConditions(disable, isAdminView, getFormData, lookupsData),
          errors,
          hiddenFields,
          itemIndex,
          isMobile,
          getFormData,
          onChange: (event: FormElementChangeEvent, memo = {}) => {
            // tmp workaround to validate date picker in the container which does not support `onBlur` event
            const { value, name, path } = event;
            if (path && (moment.isMoment(value) || isISOString(value))) {
              memo.shouldValidate = true;
            }
            // A hacky solution to handle `None of the above` in `Ethnicity` checkbox served from Master Table (DGBPRO-1642).
            if (name === ETHNICITIES_PROFILE_FIELD_NAME && Array.isArray(value)) {
              if (value[value.length - 1] === ETHNICITIES_PROFILE_FIELD_NONE_OF_ABOVE_OPTION_VALUE) {
                event.value = [ETHNICITIES_PROFILE_FIELD_NONE_OF_ABOVE_OPTION_VALUE];
              } else if (value[0] === ETHNICITIES_PROFILE_FIELD_NONE_OF_ABOVE_OPTION_VALUE) {
                event.value.shift();
              }
            }
            onChangeElement && onChangeElement(event, { shouldValidate: VALIDATE_ON_CHANGE.includes(type), ...memo });
          },
          onVisibilityChange,
          onAddItem,
          onDeleteItem,
          correctionComments,
          setMultiDocUploadInProgress,
          multiDocUploadInProgress,
        });
        const isVisible = evaluateConditions(visible, true, getFormData, lookupsData, itemIndex);
        const elementPath = getPath(name, path);
        // form-render side only
        if (
          onVisibilityChange &&
          hiddenFields &&
          ((!isVisible && !hiddenFields[elementPath]) || (isVisible && hiddenFields[elementPath]))
        ) {
          onVisibilityChange(name, elementPath);
        }

        if (!isVisible) {
          return;
        }

        index += 1;
        const key = id || name || `${index}`;
        props.key = key;

        const Comp: React.ComponentType<any> | null = getComponentByType(element);

        if (!Comp) {
          return <div key="no-component">No matching component</div>;
        }

        if (VALIDATE_ON_BLUR.includes(type)) {
          props.onBlur = onBlurElement;
        }

        return <React.Fragment key={key}>{fieldRenderer(type, props, error, Comp, index)}</React.Fragment>;
      })}
    </>
  );
});

export default ElementList;
